// @dynamic-i18n-keys
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BiData,
  DeleteDeviationConfirmClickBiEvent,
  UnlistItemConfirmBiEvent,
} from '@wix/da-bi/pkg/events';
import { PapiDeviation } from '@wix/da-papi-types';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import { PremiumTrash } from '@wix/da-ds/pkg/Icons/Confirmations';
import {
  deviationTypeToTranslationKey,
  getDeviationResourceType,
  getDeviationType,
} from '../../../../DeviationViews/_helpers';
import ConfirmationModal, {
  type Props as ConfirmationModalProps,
} from '../../../../Popup/ConfirmationModal';
import RadioButton from '@wix/da-ds/pkg/RadioButton';

import { sanitizeHref } from "@wix/da-url";

import s from './DeleteConfirmationModal.scss';

interface Props extends Omit<
  ConfirmationModalProps,
  | 'title'
  | 'text'
  | 'confirmBtnLabel'
  | 'confirmBtnType'
  | 'confirmBtnBiData'
  | 'confirmBtnDisabled'
  | 'cancelBtnBiData'
  | 'cancelBtnLabel'
  | 'doNotShowAgainKey'
  | 'autoAcceptForDoNotShow'
  | 'onDoNotShowAgainChange'
  | 'withCancelButton'
  | 'icon'
  | 'onConfirm'
> {
  deviation: PapiDeviation;
  onConfirm?: (shouldUnpublishInstead: boolean) => void;
}

const DeleteConfirmationModal: React.FC<Props> = ({
  deviation,
  onConfirm,
  ...rest
}) => {
  const { t } = useTranslation();
  const [adoptableConfirmActionValue, setAdoptableConfirmActionValue] =
    useState<'unpublish' | 'delete'>('unpublish');

  const isAdopted =
    (deviation.isAdoptable &&
      deviation.adoptable?.owner &&
      deviation.adoptable?.owner.username !== deviation.author?.username) ??
    false;

  const shouldUnpublishInstead =
    isAdopted &&
    !!deviation.isPublished &&
    adoptableConfirmActionValue === 'unpublish';

  const isConfirmable = isAdopted && !deviation.isPublished;
  const [isConfirmChecked, setIsConfirmChecked] = useState(true);
  const toggleIsConfirmChecked = useCallback(() => {
    setIsConfirmChecked(!isConfirmChecked);
  }, [isConfirmChecked]);

  const type = getDeviationType(deviation);
  const typeName = t(deviationTypeToTranslationKey(type));

  const handleConfirmation = useCallback(() => {
    onConfirm && onConfirm(shouldUnpublishInstead);
  }, [onConfirm, shouldUnpublishInstead]);

  const confirmBtnBiData = useMemo(() => {
    if (isAdopted && deviation.isPublished) {
      return BiData<UnlistItemConfirmBiEvent>({
        evid: 359,
        itemid: deviation.deviationId,
        typeid: getDeviationResourceType(deviation),
        sectionname: 'action_bar',
      });
    }
    return BiData<DeleteDeviationConfirmClickBiEvent>({
      evid: 376,
      itemid: deviation.deviationId,
      typeid: getDeviationResourceType(deviation),
    });
  }, [deviation, isAdopted]);

  return (
    <ConfirmationModal
      title={t('actionbar.delete.confirm', { type: typeName })}
      text={
        isAdopted ? (
          deviation.isPublished ? (
            <div className={s['with-adoptable-options']}>
              <p>
                {t('actionbar.delete.withAdoptableUnpublishOption.description')}
              </p>
              <RadioButton
                name="adoptable-action"
                size="small"
                checked={adoptableConfirmActionValue === 'unpublish'}
                id="delete-confirm"
                value="unpublish"
                className={s['radio-button']}
                onChange={() => setAdoptableConfirmActionValue('unpublish')}
              >
                {t('actionbar.delete.withAdoptableUnpublishOption.unpublish', {
                  postProcess: ['reactPostprocessor'],
                  learnMoreLink: (
                    <a
                      className="text-link-level-2-secondary"
                      href={sanitizeHref(t(
                        'actionbar.delete.withAdoptableUnpublishOption.learnMoreLink.url'
                      ))}
                    >
                      {t(
                        'actionbar.delete.withAdoptableUnpublishOption.learnMoreLink.text'
                      )}
                    </a>
                  ),
                })}
              </RadioButton>
              <RadioButton
                name="adoptable-action"
                size="small"
                id="delete-confirm"
                checked={adoptableConfirmActionValue === 'delete'}
                value="delete"
                className={s['radio-button']}
                onChange={() => setAdoptableConfirmActionValue('delete')}
              >
                {t(
                  'actionbar.delete.withAdoptableUnpublishOption.deleteConsent'
                )}
              </RadioButton>
            </div>
          ) : (
            <div>
              {t('actionbar.delete.cantGoBack.adopted')}
              <BooleanInput
                type="checkbox"
                size="small"
                id="delete-confirm"
                value={isConfirmChecked}
                className={s['checkbox']}
                label={t('actionbar.delete.cantGoBack.adopterConsent')}
                labelClassName={s['checkbox-label']}
                onChange={toggleIsConfirmChecked}
              />
            </div>
          )
        ) : (
          t('actionbar.delete.cantGoBack')
        )
      }
      confirmBtnLabel={
        shouldUnpublishInstead
          ? t('actionbar.unpublish')
          : t('actionbar.delete')
      }
      cancelBtnLabel={t('popup.cancel')}
      variant={isAdopted ? 'horizontal' : undefined}
      icon={!(isAdopted && !isConfirmable) && <PremiumTrash />}
      confirmBtnType={shouldUnpublishInstead ? 'default' : 'danger'}
      confirmBtnDisabled={isConfirmable && !isConfirmChecked}
      confirmBtnBiData={confirmBtnBiData}
      onConfirm={handleConfirmation}
      {...rest}
    />
  );
};

DeleteConfirmationModal.displayName = 'DeleteConfirmationModal';

export default DeleteConfirmationModal;
